import { Injectable } from '@angular/core';

import { Observable, distinctUntilChanged, map } from 'rxjs';

import { DomainQuery } from '@common/store/domain.store';
import { environment } from '@environments/environment';

/**
 * Single read point for the cookie-session / signed-cookie-media migration
 * master toggle (AK-476).
 *
 * The migration is deliberately FE-controlled and response-driven: in-flow
 * behaviour is decided by the API responses themselves (auth from the `roles`
 * in GET /api/auth/session; media mode from POST /api/media/grant returning 200
 * for cookies vs 204 for signed_url). The API does NOT surface a cookie-session
 * enable flag to the browser — the dual cookie/Bearer fallback is automatic
 * server-side. So this service exposes just ONE toggle: whether the new
 * cookie-session bootstrap path runs at all, letting us ship dark and enable
 * per environment.
 *
 * Resolution order (first defined wins):
 *   1. environment.featureOverrides.cookie_session_migration_enabled — local-dev
 *      escape hatch to force the path on/off regardless of the domain.
 *   2. The TOP-LEVEL `features` node on /api/domain — cookie mode is on when the
 *      domain has `auth_cookie_session_enabled` OR `auth_broker_enabled` (mirrors
 *      the API capability check). Lets ops roll out per tenant, no FE redeploy.
 *   3. false — default OFF (today's OIDC behaviour, ships dark).
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  constructor(private domainQuery: DomainQuery) {}

  /** Whether the cookie-session bootstrap path should run. */
  get cookieSessionMigrationEnabled(): boolean {
    return this.resolve();
  }

  /** Reactive form, emits on domain change. */
  readonly cookieSessionMigrationEnabled$: Observable<boolean> = this.domainQuery.domain$.pipe(
    map(() => this.resolve()),
    distinctUntilChanged(),
  );

  private resolve(): boolean {
    const override = environment.featureOverrides?.cookie_session_migration_enabled;
    if (typeof override === 'boolean') {
      // Explicit escape hatch: set `false` to exercise the (now dormant) OIDC/JWT
      // bootstrap path — e.g. for local testing or a rollback build.
      return override;
    }
    // AK-538: cookie-session is now the HARD DEFAULT. It no longer depends on the
    // /api/domain `features` node (which itself needs auth to load — the JWT
    // bootstrap chicken-and-egg that caused the 401 regression). The old
    // domain-driven resolution is retained below for rollback context:
    //   const features = (domain ?? this.domainQuery.getValue())?.features;
    //   return !!(features?.auth_cookie_session_enabled || features?.auth_broker_enabled);
    return true;
  }
}
